import $ from 'jquery';
import voca from 'voca';
import { getLogger } from '@cld/upload-widget';
import { getUploadFieldFromContainer, getUploadSource, getCoordinatesString, getFileTooLargeMessage } from '../common/utils';
import { types as ats } from '../store/actions';
import { selectConsoleInfo } from '../store/selectors';
import * as fileMethods from './uploadFileMethods';
import { updateCropData } from './cropDataUpdater';

const ERR_TOO_LARGE_RGX = /got (\d*)\. maximum is (\d*)\./i;

const logger = getLogger();

export const UPLOADER_EVENTS = {
  FILE_UPLOAD_PROGRESS: 'fileuploadprogress',
  FILE_UPLOAD_PROCESS_DONE: 'fileuploadprocessdone',
  FILE_UPLOAD_PROCESS_FAIL: 'fileuploadprocessfail',
  FILE_PROCESS_ALWAYS: 'fileuploadprocessalways',
  FILE_UPLOAD_ADDED: 'fileuploadadd',
  FILE_UPLOAD_FAIL: 'fileuploadfail',
  FILE_UPLOAD_DONE: 'fileuploaddone',
  FILE_UPLOAD_CHANGE: 'fileuploadchange',
  FILE_UPLOAD_PASTE: 'fileuploadpaste',
  FILE_UPLOAD_DROP: 'fileuploaddrop',
  FILE_UPLOAD_SUBMIT: 'fileuploadsubmit',
  FILE_UPLOAD_CHUNK_BEFORE_SEND: 'fileuploadchunkbeforesend',
};

const getUploadOptionsForFormData = (data, options) => {
  const uploadOptions = options.getUploadOptions();
  const tags = uploadOptions.tags ? uploadOptions.tags.join(',') : '';
  const fileData = fileMethods.getFileDataForId(data.uniqueId, options);

  return {
    tags: !voca.isBlank(tags) ? tags : undefined, // intentionally using undefined & not null
    publicId: !voca.isBlank(fileData.publicId) ? fileData.publicId : undefined, // intentionally using undefined & not null
    uploadPreset: !voca.isBlank(uploadOptions.uploadPreset) ? uploadOptions.uploadPreset : undefined, // intentionally using undefined & not null
    headers: fileData.headers,
    filenameOverride: fileData.filenameOverride,
    folder: fileData.folder,
  };
};

const prepareFormData = (data, options) => {
  const fdUploadOptions = getUploadOptionsForFormData(data, options);
  const config = fileMethods.getQueueConfigForId(data.uniqueId, options);

  if (fdUploadOptions.tags) {
    data.formData.tags = fdUploadOptions.tags;
  } else {
    delete data.formData.tags;
  }

  if (fdUploadOptions.publicId) {
    data.formData.public_id = fdUploadOptions.publicId; // public_id is the way the server accepts the value
  } else {
    delete data.formData.public_id;
  }

  if (fdUploadOptions.uploadPreset) {
    data.formData.upload_preset = fdUploadOptions.uploadPreset; // upload_preset is the way the server accepts the value
  }

  const dataFile = data.files && data.files.length && data.files[0];

  if (dataFile) {
    const fileObject = fileMethods.getFileObjectForId(data.uniqueId, options);

    if (voca.isString(fileObject)) {
      data.formData.file = fileObject;
    }

    if (fdUploadOptions.headers) {
      data.formData.headers = fdUploadOptions.headers;
    }

    if (fdUploadOptions.filenameOverride) {
      data.formData.filename_override = fdUploadOptions.filenameOverride;
    }

    if (fdUploadOptions.folder) {
      const isFolderDecoupled = typeof config?.assetFolder === 'string';
      if (isFolderDecoupled) {
        data.formData.asset_folder = (config?.assetFolder || '') + (fdUploadOptions.folder || '');
      } else {
        data.formData.folder = (config?.folder || '') + (fdUploadOptions.folder || '');
      }
    }
  }

  delete data.formData.callback; // make sure callback isnt passed to the server (not needed anymore)

  if (!data.formData.source) {
    data.formData.source = getUploadSource(selectConsoleInfo(options.getState()));
  }

  const prepResponse = options.updatePreparedParams(fileMethods.getFilePreparedParams(data.uniqueId, options));

  const customCoordinates = fileMethods.getCustomCoordinates(data.uniqueId, options);

  if (customCoordinates && config) {
    data.formData[`${config.croppingCoordinatesMode}_coordinates`] = getCoordinatesString(customCoordinates);
  }

  const fileMetadata = fileMethods.getMetadataForFile(data.uniqueId, options);

  if (fileMetadata) {
    data.formData.metadata = fileMetadata;
  }

  // ensure file's data is updated with the upload url defined by the prepare params in case its different
  data.url = prepResponse.uploadUrl !== data.url ? prepResponse.uploadUrl : data.url;
};

const parseJsonErrorMessage = (response) => {
  let msg = JSON.parse(response).error.message;
  const tooLargeMatch = ERR_TOO_LARGE_RGX.exec(msg);

  if (tooLargeMatch && tooLargeMatch.length > 2) {
    msg = getFileTooLargeMessage(tooLargeMatch[1], tooLargeMatch[2]) || msg;
  }

  return msg;
};

const getFailMessage = (data, options) => {
  let msg = 'error';

  if (data.dataType === 'json' && data.jqXHR && data.jqXHR.responseText) {
    try {
      msg = parseJsonErrorMessage(data.jqXHR.responseText);
    } catch (ex) {
      logger.warn('[uploader.uploadCallbacks]: failed to parse fail message');
    }
  } else if (isTimeout(data, options) && isLargeFile(data)) {
    msg = 'timeout';
  } else if (data.jqXHR && data.jqXHR.statusText) {
    msg = data.jqXHR.statusText;
  } else if (data.errorThrown) {
    msg = data.errorThrown;
  }

  return msg;
};

const isLargeFile = (data) => {
  const gigabyte = 1073741824;
  return data.total > 10 * gigabyte;
};

const isTimeout = (data, options) => {
  const second = 1000;
  const minute = 60 * second;
  const fileData = fileMethods.getFileDataForId(data.uniqueId, options);
  if (fileData?.uploadedAt) {
    const uploadTime = Date.now() - fileData.uploadedAt;
    return uploadTime > 10 * minute - 10 * second;
  }
  return false;
};

const onUploadDone = (container, options, e, data) => {
  logger.log(`[uploader.callbacks] -UPLOAD_DONE-: upload done for file: ${data.uniqueId}`);
  fileMethods.finalizeFileUpload(data.uniqueId, true, data.result, options);
};

const onUploadAdded = (container, options, e, data) => {
  const fileId = data.files[0].id;

  if (fileId) {
    logger.log(`[uploader.uploadCallbacks]: upload added with id: ${fileId} `, data);
    const fileObject = fileMethods.getFileObjectForId(fileId, options);

    data.uniqueId = fileId;

    if (fileObject) {
      data.files[0] = fileObject;
      fileMethods.updateFileDataMethods(fileId, data, options);
      fileMethods.setAllowedFileTypesForFile(fileId, fileObject, container, options);
    } else {
      logger.warn(`[uploader.callbacks] -UPLOAD_ADD-: failed to get file object for id: ${fileId}`, data);
    }
  } else {
    logger.error('[uploader.callbacks] -UPLOAD_ADD-: invalid data information added !!!!!!!! ', data);
  }
};

const onAdd = (container, options, e, data, field) => {
  if (!e.isDefaultPrevented()) {
    field = $(field);

    data.process(() => {
      field.fileupload('process', data);
    }); // kick off the processing tasks in the file upload process queue

    if (typeof data.files[0] === 'string' && options.config.multiple) {
      // A fix for: CLD-3600 Multiple uploads duplicate the same image. Add debouncing for multiple upload urls.
      const processQueue = field.fileupload('option', 'processQueue');
      if (!processQueue.some(({ action }) => action === 'debounce')) {
        processQueue.push({ action: 'debounce' });
        field.fileupload('option', 'processQueue', processQueue);
      }
    }
  }
};

const onUploadProcessedAlways = (container, options, e, data) => {
  if (fileMethods.isFileInCurrentQueue(data.uniqueId, options)) {
    logger.log(`[uploader.uploadCallbacks] -UPLOAD_PROCESSED-: upload processed for file: ${data.uniqueId} `, data);

    options.actions[ats.SET_UPLOADER_QUEUE_RUNNING_FOR_FILE]({ id: data.uniqueId }); // set the queue status as running at this point

    if (data.formData) {
      prepareFormData(data, options);
    }

    if (!data.files[0].error) {
      data.submit();
    }
  }
};

const onUploadProgress = (container, options, e, data) => {
  const fileData = fileMethods.getFileDataForId(data.uniqueId, options);

  if (!fileData.isFetch) {
    const progress = Math.round((data.loaded * 100.0) / data.total);
    logger.log(`[uploader.uploadCallbacks] -UPLOAD_PROGRESS-: upload progress update for file: ${data.uniqueId}, progress: ${progress}`);
    fileMethods.updateFileProgress(data.uniqueId, progress, options);
  } else {
    // Immediately set progress to 100% and show processing spinner
    logger.log(`[uploader.uploadCallbacks] -UPLOAD_PROGRESS-: skip upload progress update for fetched file: ${data.uniqueId}`);
    fileMethods.updateFileProgress(data.uniqueId, 100, options);
  }
};

const onUploadProcessDone = (container, options, e, data) => {
  logger.log(`[uploader.uploadCallbacks] -UPLOAD_PROCESS_DONE-: processing done for file: ${data.uniqueId}`);

  if (data.files && data.files.length) {
    if (fileMethods.isFileInQueues(data.uniqueId, options)) {
      updateCropData(data, options);

      fileMethods.updateFileDataMethods(data.uniqueId, data, options);
      fileMethods.updateFilePreview(data.uniqueId, data, options);
    }
  }
};

const onUploadFailed = (container, options, e, data) => {
  const result = getFailMessage(data, options);

  logger.warn(`[uploader.uploadCallbacks] -UPLOAD_FAILED-: encountered upload failure for file: ${data.uniqueId}`, result, data);

  if (fileMethods.isFileInQueues(data.uniqueId, options)) {
    fileMethods.finalizeFileUpload(data.uniqueId, false, result, options);
  }
};

const onUploadChunkBeforeSend = (container, options, e, data) => {
  if (data.formData) {
    prepareFormData(data, options);
  }
};

const onUploadProcessFailed = (container, options, e, data) => {
  const err = data.files[0].error;

  if (err) {
    logger.warn(`[uploader.uploadCallbacks] -UPLOAD_PROCESS_FAIL-:
                encountered upload processing failure for file: ${data.uniqueId}`);

    fileMethods.finalizeFileUpload(data.uniqueId, false, err, options);
  } else {
    logger.warn('[uploader.uploadCallbacks]: process failed without error message', data);
    onUploadProcessDone(container, options, e, data);
  }
};

const onUploadSubmit = (container, options, e, data) => {
  let canSubmit = true;
  logger.log(`[uploader.uploadCallbacks] -UPLOAD_SUBMIT-: about to submit file: ${data.uniqueId}`);

  if (fileMethods.isFileInCurrentQueue(data.uniqueId, options)) {
    const fileData = fileMethods.getFileDataForId(data.uniqueId, options);

    if (fileData.delayedPreCalls) {
      // pre-calls were delayed so need to make sure
      // unset the precalls delayed flag so we dont get into this conditional next time
      fileMethods.unsetFilePreCallsDelayed([data.uniqueId], options);

      canSubmit = false;
      logger.log('[uploader.uploadCallbacks] -UPLOAD_SUBMIT-: delaying submit - coordinates changed due to resize');

      setTimeout(() => {
        // re-add to the queue for batch handler to run pre-batch and prepare if configured
        options.handleUpload(
          {
            files: [fileData],
            cropInfo: [fileData.customCoordinates],
          },
          true,
          true
        );
      }, 1);
    }

    if (canSubmit && fileData.isFetch) {
      fileMethods.initFetchUploadProgress(data.uniqueId, options);
    }
  }

  return canSubmit;
};

const onBatchAdded = (options, e, data) => {
  logger.log(`[uploader.callbacks]: new batch [${data.files.length}] was added to be uploaded`);
  options.handleUpload(data, true);
  return false; // cancelling the initial add to allow prebatch logic to take place first
};

export default (container, options) => {
  logger.log('[uploader.callbacks]: setting up callbacks on uploader');

  const useCustomAddMethod = () =>
    getUploadFieldFromContainer(container).fileupload('option', 'add', function (e, data) {
      // must overwrite the add function for processing to work correctly before file is submitted to be uploaded !
      onAdd(container, options, e, data, this);
    });

  const UE = UPLOADER_EVENTS;

  getUploadFieldFromContainer(container)
    .on(UE.FILE_UPLOAD_ADDED, onUploadAdded.bind(null, container, options))
    .on(UE.FILE_UPLOAD_PROGRESS, onUploadProgress.bind(null, container, options))
    .on(UE.FILE_UPLOAD_FAIL, onUploadFailed.bind(null, container, options))
    .on(UE.FILE_UPLOAD_PROCESS_DONE, onUploadProcessDone.bind(null, container, options))
    .on(UE.FILE_UPLOAD_PROCESS_FAIL, onUploadProcessFailed.bind(null, container, options))
    .on(UE.FILE_PROCESS_ALWAYS, onUploadProcessedAlways.bind(null, container, options))
    .on(UE.FILE_UPLOAD_SUBMIT, onUploadSubmit.bind(null, container, options))
    .on(UE.FILE_UPLOAD_DONE, onUploadDone.bind(null, container, options))
    .on(UE.FILE_UPLOAD_CHUNK_BEFORE_SEND, onUploadChunkBeforeSend.bind(null, container, options))
    .on(`${UE.FILE_UPLOAD_DROP} ${UE.FILE_UPLOAD_CHANGE} ${UE.FILE_UPLOAD_PASTE}`, onBatchAdded.bind(null, options));

  useCustomAddMethod();

  return {
    useCustomAddMethod,
  };
};
